import { IconCaretDownFilled, IconHelpCircle } from '@tabler/icons-react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { Box, Text, useTheme } from 'app/shopify/theme';
import { TabBarItem, type NavigationItem } from '../TabBarItem';
import { Button } from '../../actions/Button';
import { PublishButton } from '../../actions/PublishButton';
import type { PublishAction } from '../Navigation/PublishButton';
import { NavigationPressable } from '../Navigation/NavigationPressable';
import { HeaderSearch, type HeaderSearchProps } from './HeaderSearch';

export interface HeaderAction {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}

export interface HeaderBarProps {
  variant?: 'main' | 'flow';
  /** Brand-owned artwork; callers choose the appropriate white-label logo. */
  logo: React.ReactNode;
  search?: HeaderSearchProps;
  publish?: PublishAction;
  signedIn?: boolean;
  items?: readonly NavigationItem[];
  selectedKey?: string;
  onSelect?: (key: string) => void;
  signIn?: HeaderAction;
  help?: HeaderAction;
  language?: HeaderAction;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
}

function SignedOutActions({
  publish,
  signIn,
  help,
  language,
  disabled,
}: Pick<HeaderBarProps, 'publish' | 'signIn' | 'help' | 'language' | 'disabled'>) {
  const theme = useTheme();
  const radius = theme.borderRadii['radius.full'];
  const color = theme.colors['white-label.header.on-header'];
  return (
    <>
      <Box flexDirection="row" flexWrap="wrap" alignItems="center" gap="spacing.general.x2" maxWidth="100%">
        {publish && (
          <PublishButton {...publish} size="medium" breakpoint="large" disabled={disabled || publish.disabled} />
        )}
        {signIn && (
          <Button
            label={signIn.label}
            variant="tertiary"
            size="medium"
            onPress={signIn.onPress}
            disabled={disabled || signIn.disabled}
          />
        )}
      </Box>
      {help && (
        <NavigationPressable
          accessibilityLabel={help.label}
          onPress={help.onPress}
          disabled={disabled || help.disabled}
          style={{
            width: Number(theme.tokens.sizing['sizing.component.button.height.small']),
            height: Number(theme.tokens.sizing['sizing.component.button.height.small']),
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: radius,
          }}
        >
          <IconHelpCircle size={Number(theme.tokens.sizing['sizing.icon.sm'])} color={color} strokeWidth={2} />
        </NavigationPressable>
      )}
      {language && (
        <NavigationPressable
          accessibilityLabel={language.label}
          onPress={language.onPress}
          disabled={disabled || language.disabled}
          style={{
            minHeight: Number(theme.tokens.sizing['sizing.component.button.height.small']),
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing['spacing.general.x1'],
            borderRadius: theme.borderRadii['radius.sm'],
          }}
        >
          <Text variant="bodySmallMedium" color="white-label.header.on-header">
            {language.label}
          </Text>
          <IconCaretDownFilled size={Number(theme.tokens.sizing['sizing.icon.s'])} color={color} />
        </NavigationPressable>
      )}
    </>
  );
}

export function HeaderBar({
  variant = 'main',
  logo,
  search,
  publish,
  signedIn = true,
  items = [],
  selectedKey,
  onSelect,
  signIn,
  help,
  language,
  disabled = false,
  style,
}: HeaderBarProps) {
  const theme = useTheme();
  const main = variant === 'main';
  return (
    <Box
      backgroundColor="white-label.header.bg"
      paddingHorizontal="spacing.semantic.page-margin"
      alignItems="center"
      width="100%"
      style={[
        {
          // Shadow/md has two untokenized offsets and blur radii in the Figma effect style.
          boxShadow: [
            { offsetX: 0, offsetY: 2, blurRadius: 4, color: theme.colors['shadow.md'] },
            { offsetX: 0, offsetY: 4, blurRadius: 16, color: theme.colors['shadow.sm'] },
          ],
        },
        style,
      ]}
    >
      <Box
        width="100%"
        maxWidth={Number(theme.tokens.sizing['sizing.component.header.max-width'])}
        minHeight={Number(theme.tokens.sizing['sizing.general.x18'])}
        flexDirection="row"
        flexWrap="wrap"
        alignItems="center"
        alignContent="center"
        gap="spacing.general.x6"
        paddingVertical="spacing.general.x2"
      >
        <Box
          width={Number(theme.tokens.sizing['sizing.general.x40'])}
          minHeight={Number(theme.tokens.sizing['sizing.general.x10'])}
          justifyContent="center"
        >
          {logo}
        </Box>
        {main && search && (
          <Box flexGrow={1} flexShrink={1} flexBasis={Number(theme.tokens.sizing['sizing.general.x40'])} minWidth={0}>
            <HeaderSearch {...search} disabled={disabled || search.disabled} />
          </Box>
        )}
        {main && (
          <Box
            flexDirection="row"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="flex-end"
            gap="spacing.general.x4"
            flexShrink={1}
            style={{ marginLeft: search ? undefined : 'auto' }}
          >
            {signedIn && publish && (
              <PublishButton {...publish} size="medium" breakpoint="large" disabled={disabled || publish.disabled} />
            )}
            {signedIn ? (
              <Box flexDirection="row" flexWrap="wrap" alignItems="center">
                {items.map(({ key, ...item }) => (
                  <TabBarItem
                    key={key}
                    {...item}
                    placement="header"
                    active={selectedKey === key}
                    disabled={disabled || item.disabled}
                    onPress={() => onSelect?.(key)}
                  />
                ))}
              </Box>
            ) : (
              <SignedOutActions publish={publish} signIn={signIn} help={help} language={language} disabled={disabled} />
            )}
          </Box>
        )}
      </Box>
      <Box
        pointerEvents="none"
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        height={theme.borderWidths['border-width.subtle']}
        backgroundColor="border.divider"
      />
    </Box>
  );
}
